import * as React from 'react';
import '../styles/reset.css';
import '../styles/global.css';
import { withPrefix, useStaticQuery, graphql } from 'gatsby';
import { PrimaryButton, DefaultButton } from '@fluentui/react';

import {
    BigPromoStyled,
    ContactUsStyled,
    DashboardImageStyled, FaqStyled,
    FeaturesStyled,
    HeaderStyled,
    LoginSectionStyled,
    LogoStyled,
    MainSectionStyled,
    MainStyled,
    MenuStyled,
    SupportsStyled, UptimeStyled
} from '../styles/styled';
import { topMenu } from '../data/menu';
import FeatureList from '../components/featureList';
import { Helmet } from 'react-helmet';
import Faq from '../components/faq';
import Supports from '../components/supports';
import TotalServersCount from '../components/totalServersCount';
import Uptime from '../components/uptime';

const navigater = (e, uri) => {
    e.preventDefault();
    const targetId = (uri || '').replace(/^#/, '');
    setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
};

const IndexPage = () => {
  const brand = '#00BCD4';
  const brandHover = '#00E5FF';
  const primaryButtonStyles = {
    root: {
      background: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
      border: 'none',
      color: '#ffffff',
      borderRadius: 10,
      padding: '12px 32px',
      fontSize: 16,
      fontWeight: 600,
      boxShadow: '0 4px 16px rgba(0, 188, 212, 0.4)',
      transition: 'all 0.3s ease',
    },
    rootHovered: {
      background: 'linear-gradient(135deg, #00E5FF 0%, #00BCD4 100%)',
      color: '#ffffff',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 24px rgba(0, 188, 212, 0.6)',
    },
    rootPressed: {
      background: 'linear-gradient(135deg, #0097A7 0%, #00838F 100%)',
      color: '#ffffff',
      transform: 'translateY(0)',
    },
    label: { fontWeight: 600, color: '#ffffff' }
  };
  const defaultButtonStyles = {
    root: {
      background: 'rgba(0, 188, 212, 0.1)',
      borderColor: 'rgba(0, 188, 212, 0.4)',
      color: '#00E5FF',
      borderRadius: 10,
      padding: '12px 32px',
      fontSize: 16,
      fontWeight: 600,
      transition: 'all 0.3s ease',
    },
    rootHovered: {
      background: 'rgba(0, 188, 212, 0.2)',
      borderColor: 'rgba(0, 229, 255, 0.6)',
      color: '#00E5FF',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0, 188, 212, 0.3)',
    },
    rootPressed: {
      background: 'rgba(0, 188, 212, 0.25)',
      borderColor: 'rgba(0, 229, 255, 0.7)',
      color: '#00E5FF',
      transform: 'translateY(0)',
    },
    label: { fontWeight: 600, color: '#00E5FF' }
  };

    const { site } = useStaticQuery(graphql`
      query HomeSeoQuery {
        site {
          siteMetadata {
            title
            description
            siteUrl
            author
            language
            keywords
          }
        }
      }
    `);

    const meta = site?.siteMetadata || {};
    const canonicalUrl = meta.siteUrl || 'https://syagent.com';
    const ogImage = withPrefix('/images/d.png'); // TODO: replace with a 1200x630 image for best sharing previews

    return (
        <MainSectionStyled>
            <Helmet defer={false}>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="robots" content="index,follow,max-image-preview:large" />

                <title>{meta.title || 'SyAgent — Free server and uptime monitoring'}</title>
                <meta name="description" content={meta.description || 'Free easy Linux server and uptime monitoring in a few seconds. Alarms, charts, CPU, memory, disks, sessions, network.'} />
                <meta name="author" content={meta.author || 'SyAgent'} />
                <meta name="language" content={meta.language || 'en'} />
                {meta.keywords && <meta name="keywords" content={meta.keywords} />}

                {/* Canonical */}
                <link rel="canonical" href={canonicalUrl} />
                <link rel="alternate" href={canonicalUrl} hrefLang={meta.language || 'en'} />

                {/* Open Graph */}
                <meta property="og:site_name" content={meta.title || 'SyAgent'} />
                <meta property="og:title" content={meta.title || 'SyAgent — Free server and uptime monitoring'} />
                <meta property="og:description" content={meta.description || 'Free easy Linux server and uptime monitoring in a few seconds.'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:locale" content="en_US" />
                <meta property="og:image" content={ogImage} />
                <meta property="og:image:alt" content="SyAgent dashboard preview" />
                <meta property="og:image:type" content="image/png" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={meta.title || 'SyAgent — Free server, uptime and mongodb monitoring'} />
                <meta name="twitter:description" content={meta.description || 'Free easy Linux server and uptime monitoring in a few seconds.'} />
                <meta name="twitter:image" content={ogImage} />

                {/* Apple / PWA */}
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
                <meta name="apple-mobile-web-app-title" content="SyAgent" />
                <link rel="apple-touch-icon" href={withPrefix('/images/apple-touch-icon.png')} />

                {/* Preconnects */}
                <link rel="preconnect" href="https://app.syagent.com" />
                <link rel="dns-prefetch" href="https://app.syagent.com" />

                {/* Structured Data */}
                <script type="application/ld+json">{JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'WebSite',
                  name: meta.title || 'SyAgent',
                  url: canonicalUrl,
                  potentialAction: {
                    '@type': 'SearchAction',
                    target: `${canonicalUrl}/?q={query}`,
                    'query-input': 'required name=query'
                  }
                })}</script>
                <script type="application/ld+json">{JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'Organization',
                  name: 'SyAgent',
                  url: canonicalUrl,
                  logo: withPrefix('/images/favicon.png')
                })}</script>
            </Helmet>
            <HeaderStyled>
                <LogoStyled>
                    SyAgent
                    <span>BETA</span>
                </LogoStyled>
                <MenuStyled>
                    { topMenu.map(link => {
                        return (
                            <li key={ link.title }>
                                <a
                                    onClick={ (e) => navigater(e, link.link) }
                                    href={ link.link }>{ link.title }</a>
                            </li>
                        );
                    }) }
                </MenuStyled>
                <LoginSectionStyled>
                    <PrimaryButton
                      text={'Join'}
                      href="https://app.syagent.com/register"
                      styles={primaryButtonStyles}
                    />

                    <DefaultButton
                      text={'Login'}
                      href="https://app.syagent.com/login"
                      styles={defaultButtonStyles}
                    />
                </LoginSectionStyled>
            </HeaderStyled>
            <MainStyled id="home">
                <BigPromoStyled>
                    <h1>
                        Free <span className="kanula">Server</span> and <span
                        className="kanula">Uptime</span> Monitoring Solution in few seconds
                    </h1>
                    <h2>Monitor Linux servers, processes, disks, network and uptime — alerts, charts and insights in minutes.</h2>
                    <TotalServersCount/>
                    <div className="start-for-free-wrapper">
                        <PrimaryButton
                          text={'Start'}
                          href="https://app.syagent.com/register"
                          styles={primaryButtonStyles}
                        />
                    </div>
                    <DashboardImageStyled>
                        <img src={ `${ withPrefix('/images/d.png') }` } alt=""/>
                    </DashboardImageStyled>
                </BigPromoStyled>
                <FeaturesStyled id="features">
                    <FeatureList/>
                </FeaturesStyled>
                <UptimeStyled id="uptime">
                    <Uptime/>
                </UptimeStyled>
                <SupportsStyled>
                    <Supports/>
                </SupportsStyled>
                {/*<PricingStyled id="#pricing">*/ }
                {/*    <Pricing/>*/ }
                {/*</PricingStyled>*/ }
                <FaqStyled id="faq">
                    <Faq/>
                </FaqStyled>
                <ContactUsStyled id="contact">
                    <h3>Contact us</h3>
                    <p className="subtitle">Have a question, feature request, or need help setting things up? We’re here.</p>
                    <div className="contact-grid">
                        <a className="card" href="https://app.syagent.com/register">
                            <span className="card-title">Start for free</span>
                            <span className="card-body">Create your account in seconds</span>
                        </a>
                        <a className="card" href="#faq" onClick={(e) => navigater(e, '#faq')}>
                            <span className="card-title">Read FAQ</span>
                            <span className="card-body">Answers to common questions</span>
                        </a>
                    </div>
                </ContactUsStyled>
            </MainStyled>
        </MainSectionStyled>
    );
};

export default IndexPage;
